// SourceEventsPanel — renders the aggregation source-events blob attached to an
// alert (or test result row) via `sourceEventsBlobPath`. Lazy-fetches the blob,
// summarises non-owner mailbox access (amber card per mailbox, deduped item
// table), then exposes a "Source Event Details" section with one collapsible
// Raw Event JSON viewer per source event.
//
// PHI/PII: email subject lines can contain PHI/PII (e.g. on "Anomalous access to
// other users' mailboxes" alerts). Subjects are MASKED by default everywhere on
// this panel — both the structured Subject column and inside the per-event raw
// JSON — and are only unmasked, per mailbox, after the analyst clicks "Reveal".
// Each reveal is recorded in the central portal activity log (phi.subject_reveal)
// via logSubjectView. The log captures only metadata (who / alert / mailbox /
// count / time), never the subject text.
//
// Used by AlertsPage and TestRunResultsPage expanded rows.

import React, { useState, useMemo, useCallback } from 'react';
import { CollapsibleSection } from './sharedHelpers';
import { normalizeIsoUtc, resolveTz } from '../../utils/timeFormat';
import RawEventJsonPanel from './RawEventJsonPanel';
import { parseAggregationFields, mailAccessTypeOf, syncedFolderOf } from './aggregationHelpers';
import { useSourceEvents } from './useSourceEvents';
import { logSubjectView } from '../../api/client';

// Exchange delegate logons populate the source event's UserId with a LogonUserSid
// (S-1-5-80-... or S-1-5-21-...) rather than a UPN. The detection pipeline resolves
// that SID to a UPN in the aggregation event's `delegate_upn` field — prefer that
// for display when it's available.
const SID_RE = /^S-\d-\d/;

const SUBJECT_MASK = '•••••••••••••';
const SUBJECT_HIDDEN_JSON = '(hidden — reveal subjects to view)';

// Deep-scrub any property named "Subject" (or "FlaggedSubject") out of a cloned
// source-event object so masked subjects don't leak through the raw JSON viewer.
function scrubSubjectsDeep(node) {
  if (Array.isArray(node)) { node.forEach(scrubSubjectsDeep); return; }
  if (node && typeof node === 'object') {
    for (const k of Object.keys(node)) {
      if ((k === 'Subject' || k === 'FlaggedSubject') && typeof node[k] === 'string' && node[k]) {
        node[k] = SUBJECT_HIDDEN_JSON;
      } else {
        scrubSubjectsDeep(node[k]);
      }
    }
  }
}

export default function SourceEventsPanel({ blobPath, tzIana, rawEventJson, tenantId, alertId, ruleName, source = 'alerts' }) {
  // v1.60.0: fetch state lifted to shared useSourceEvents hook so the alerts
  // quick-action modal can reuse the cached blob without re-downloading.
  const { data, loading, error } = useSourceEvents(blobPath, tenantId, alertId);
  const [expandedMailboxes, setExpandedMailboxes] = useState(new Set());

  // PHI gate — owners (mailboxes) whose subject lines the analyst has revealed.
  // Keyed by the mailbox owner string so both the structured Subject column and
  // the per-event raw JSON can check the same gate.
  const [revealedOwners, setRevealedOwners] = useState(() => new Set());

  // Collect non-PHI message identifiers for one mailbox owner straight from the
  // RAW source events (not the subject-deduped display list), so the audit record
  // ties back to every individual message whose subject was exposed. We log the
  // opaque IDs (InternetMessageId / ImmutableId / Id) + folder + size — never the
  // subject text. Deduped by id; capped to keep the Table Storage row small.
  const MESSAGE_LOG_CAP = 200;
  const collectMessagesForOwner = useCallback((owner) => {
    const out = [];
    const seen = new Set();
    let total = 0;
    for (const se of (data?.source_events || [])) {
      const seOwner = se.MailboxOwnerUPN || se.MailboxOwnerSid;
      if (!owner || !seOwner || seOwner.toLowerCase() !== owner.toLowerCase()) continue;
      let folders = se.Folders;
      if (typeof folders === 'string') { try { folders = JSON.parse(folders); } catch { folders = []; } }
      if (!Array.isArray(folders)) continue;
      for (const f of folders) {
        const folderName = f.Path || f.FolderPath || f.DisplayName || f.Name || '';
        for (const item of (f.FolderItems || [])) {
          if (item.Subject == null || item.Subject === '') continue; // only subject-bearing (PHI) items
          const id = item.InternetMessageId || item.ImmutableId || item.Id || '';
          const dedupKey = id || `${folderName}|${item.SizeInBytes ?? ''}`;
          if (seen.has(dedupKey)) continue;
          seen.add(dedupKey);
          total++;
          if (out.length < MESSAGE_LOG_CAP) {
            out.push({ id, folder: folderName, sizeKB: item.SizeInBytes ? Math.round(item.SizeInBytes / 1024) : null });
          }
        }
      }
    }
    return { messages: out, total };
  }, [data]);

  const revealMailbox = useCallback((mailbox, subjectCount) => {
    if (!mailbox) return;
    setRevealedOwners((prev) => {
      if (prev.has(mailbox)) return prev;
      const next = new Set(prev);
      next.add(mailbox);
      return next;
    });
    // Audit the PHI view (fire-and-forget; never blocks the reveal). Includes the
    // exact message identifiers + the evidence blob pointer so the record can be
    // tied to the actual messages without storing any subject text.
    const { messages, total } = collectMessagesForOwner(mailbox);
    logSubjectView({
      alertId, tenantId, mailbox, subjectCount, ruleName, source,
      blobPath, messages, messageCount: total,
    });
  }, [alertId, tenantId, ruleName, source, blobPath, collectMessagesForOwner]);

  const hideMailbox = useCallback((mailbox) => {
    setRevealedOwners((prev) => {
      if (!prev.has(mailbox)) return prev;
      const next = new Set(prev);
      next.delete(mailbox);
      return next;
    });
  }, []);

  // Pipeline-resolved delegate UPN from the aggregation event. When the source
  // event actor is a SID, prefer this for display so analysts don't have to
  // mentally map S-1-5-80-... back to the human.
  const delegateUpn = useMemo(() => {
    const agg = parseAggregationFields(rawEventJson);
    return agg?.delegate_upn || null;
  }, [rawEventJson]);

  // Substitute SID-shaped actors with the resolved delegate UPN when available.
  // Non-SID actors (UPNs, app PUIDs starting with "10037FFE..." which are not S-1
  // prefixed) pass through unchanged.
  const resolveActor = (raw) => {
    if (raw && delegateUpn && SID_RE.test(raw)) return delegateUpn;
    return raw;
  };

  // Aggregate unique mailboxes accessed (non-owner access only), with deduped items
  const mailboxSummary = useMemo(() => {
    if (!data?.source_events) return [];
    const map = new Map();
    for (const se of data.source_events) {
      const owner = se.MailboxOwnerUPN || se.MailboxOwnerSid;
      if (!owner) continue;
      const actor = se.UserId || se.UserPrincipalName;
      if (owner.toLowerCase() === actor?.toLowerCase()) continue;
      if (!map.has(owner)) map.set(owner, { events: 0, syncEvents: 0, syncFolders: new Set(), itemMap: new Map(), actorSet: new Set() });
      const entry = map.get(owner);
      entry.events++;
      if (actor) entry.actorSet.add(resolveActor(actor));
      // A Sync is Outlook's cache downloading a folder: Microsoft records the folder,
      // never the messages, so it contributes no items -- counted separately so the
      // card does not read "0 items read" as though nothing was reached.
      if (mailAccessTypeOf(se).toLowerCase() === 'sync') {
        entry.syncEvents++;
        const fname = syncedFolderOf(se);
        if (fname) entry.syncFolders.add(fname);
      }
      let folders = se.Folders;
      if (folders && typeof folders === 'string') { try { folders = JSON.parse(folders); } catch { folders = []; } }
      if (Array.isArray(folders)) {
        for (const f of folders) {
          for (const item of (f.FolderItems || [])) {
            // Group by Subject when present (SizeInBytes varies per item so can't use it as key)
            const key = item.Subject != null
              ? item.Subject
              : (item.ImmutableId || item.InternetMessageId || item.Id);
            if (!key) continue;
            if (!entry.itemMap.has(key)) {
              entry.itemMap.set(key, { ...item, _count: 1 });
            } else {
              entry.itemMap.get(key)._count++;
            }
          }
        }
      }
    }
    return Array.from(map.entries())
      .map(([mailbox, s]) => ({
        mailbox,
        events: s.events,
        syncEvents: s.syncEvents,
        syncFolders: Array.from(s.syncFolders),
        items: Array.from(s.itemMap.values()),
        actors: Array.from(s.actorSet),
      }))
      .sort((a, b) => b.items.length - a.items.length);
  }, [data, delegateUpn]);

  return (
    <div>
      <div className="flex items-center gap-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
        <span>Source Events</span>
        {data && (
          <>
            <span className="text-gray-600">—</span>
            <span className="text-gray-500 normal-case font-normal">
              {data.source_event_count} event{data.source_event_count !== 1 ? 's' : ''}
              {data.aggregation_window_utc && (
                <span className="ml-1">
                  · {data.aggregation_window_utc.start?.substring(0, 16).replace('T', ' ')}–{data.aggregation_window_utc.end?.substring(11, 16)} UTC
                </span>
              )}
            </span>
            {data.source_events_truncated && (
              <span className="px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-400 border border-amber-700/40 normal-case font-normal">
                capped at {data.source_events?.length}
              </span>
            )}
          </>
        )}
      </div>
      {loading && <p className="text-gray-500 italic text-xs py-1">Loading source events…</p>}
      {error && <p className="text-red-400 italic text-xs py-1">{error}</p>}
      {mailboxSummary.length > 0 && (
        <div className="mb-3">
          <div className="text-[11px] font-semibold text-amber-400 uppercase tracking-wide mb-1.5">
            Mailboxes Accessed — {mailboxSummary.length}
          </div>
          <div className="space-y-2">
            {mailboxSummary.map((m, i) => {
              const sorted = [...m.items].sort((a, b) => (b._count || 1) - (a._count || 1));
              const COLLAPSE_THRESHOLD = 3;
              const isExpanded = expandedMailboxes.has(i);
              const revealed = revealedOwners.has(m.mailbox);
              const subjectCount = m.items.filter((it) => it.Subject != null && it.Subject !== '').length;
              const visible = sorted.length > COLLAPSE_THRESHOLD && !isExpanded
                ? sorted.slice(0, COLLAPSE_THRESHOLD)
                : sorted;
              const hiddenCount = sorted.length - COLLAPSE_THRESHOLD;
              return (
              <div key={i} className="bg-amber-950/30 border border-amber-800/40 rounded overflow-hidden">
                {/* Mailbox header row */}
                <div className="px-3 py-1.5">
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-amber-300 font-mono flex-1 min-w-0 truncate">{m.mailbox}</span>
                    <span className="text-gray-500 shrink-0">{m.events} event{m.events !== 1 ? 's' : ''}</span>
                    {m.items.length === 0 && m.syncEvents > 0 ? (
                      <span
                        className="text-gray-400 shrink-0 font-medium"
                        title="Outlook synchronized this mailbox's folders to its local cache. Microsoft records the folder for a sync, not the individual messages, so there is no item list -- the folder's contents may have been downloaded."
                      >
                        Folder sync · messages not listed by Microsoft
                      </span>
                    ) : (
                      <span className="text-gray-400 shrink-0 font-medium">
                        {m.items.length} item{m.items.length !== 1 ? 's' : ''} read
                        {m.syncEvents > 0 && (
                          <span className="text-gray-500 font-normal" title="Folder syncs record the folder only, not the messages in it.">
                            {` + ${m.syncEvents} folder sync${m.syncEvents !== 1 ? 's' : ''}`}
                          </span>
                        )}
                      </span>
                    )}
                    {subjectCount > 0 && (
                      revealed ? (
                        <button
                          type="button"
                          onClick={() => hideMailbox(m.mailbox)}
                          className="shrink-0 text-[11px] text-gray-400 hover:text-gray-200 border border-gray-700 hover:border-gray-600 rounded px-1.5 py-0.5"
                          title="Re-mask the subject lines"
                        >
                          Hide subjects
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => revealMailbox(m.mailbox, subjectCount)}
                          className="shrink-0 text-[11px] text-amber-300 hover:text-amber-200 border border-amber-700/60 hover:border-amber-600 bg-amber-900/30 rounded px-1.5 py-0.5"
                          title="Subjects may contain PHI/PII. Revealing is recorded in the activity log."
                        >
                          Reveal {subjectCount} subject{subjectCount !== 1 ? 's' : ''} (logged)
                        </button>
                      )
                    )}
                  </div>
                  {m.actors.length > 0 && (
                    <div className="mt-0.5 text-[11px] font-mono">
                      <span className="text-gray-500">Accessed by:</span>{' '}
                      <span className="text-sky-300 break-all">{m.actors.join(', ')}</span>
                    </div>
                  )}
                  {m.syncFolders.length > 0 && (
                    <div className="mt-0.5 text-[11px]">
                      <span className="text-gray-500">Synced folders:</span>{' '}
                      <span className="text-gray-300 break-all">{m.syncFolders.join(', ')}</span>
                    </div>
                  )}
                </div>
                {/* Individual items */}
                {m.items.length > 0 && (
                  <div className="border-t border-amber-800/30">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="bg-amber-950/40">
                          <th className="text-left text-gray-500 font-medium px-3 py-1 w-full">Subject</th>
                          <th className="text-right text-gray-500 font-medium px-3 py-1 whitespace-nowrap">Size</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visible.map((item, j) => {
                          const hasSubject = item.Subject != null && item.Subject !== '';
                          // PHI gate: subject text is shown only after the mailbox is revealed.
                          const masked = hasSubject && !revealed;
                          const display = !hasSubject
                            ? '(no subject)'
                            : (masked ? SUBJECT_MASK : item.Subject);
                          return (
                          <tr key={j} className="border-t border-amber-900/20 hover:bg-amber-900/10">
                            <td className="px-3 py-1 text-gray-300 max-w-0">
                              <span className="flex items-center gap-1.5 min-w-0">
                                <span
                                  className={`truncate${masked ? ' text-gray-600 select-none tracking-widest' : ''}${!hasSubject ? ' italic text-gray-500' : ''}`}
                                  title={masked || !hasSubject ? undefined : item.Subject}
                                >
                                  {display}
                                </span>
                                {item._count > 1 && <span className="shrink-0 text-gray-500">×{item._count}</span>}
                              </span>
                            </td>
                            <td className="px-3 py-1 text-gray-500 text-right whitespace-nowrap">
                              {item.SizeInBytes ? `${Math.round(item.SizeInBytes / 1024)} KB` : '—'}
                            </td>
                          </tr>
                          );
                        })}
                        {sorted.length > COLLAPSE_THRESHOLD && (
                          <tr className="border-t border-amber-900/20">
                            <td colSpan={2} className="px-3 py-1">
                              <button
                                className="text-[11px] text-amber-500 hover:text-amber-300"
                                onClick={() => setExpandedMailboxes(prev => {
                                  const next = new Set(prev);
                                  isExpanded ? next.delete(i) : next.add(i);
                                  return next;
                                })}
                              >
                                {isExpanded ? 'Show less' : `Show ${hiddenCount} more…`}
                              </button>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}
      {data?.source_events && data.source_events.length > 0 && (
        <CollapsibleSection title="Source Event Details">
          {data.source_events.map((se, idx) => {
            const ts = se.CreationTime || se.EventTimeUTC || se.eventTimestamp;
            const actor = resolveActor(se.UserId || se.UserPrincipalName);
            const owner = se.MailboxOwnerUPN;
            // Show "actor → owner" when the two differ (non-owner access — the
            // case this panel exists for); fall back to just the owner when
            // they match or the actor is missing.
            const who = actor && owner && actor.toLowerCase() !== owner.toLowerCase()
              ? `${actor} → ${owner}`
              : (owner || actor || null);
            const label = [
              ts ? new Date(normalizeIsoUtc(ts)).toLocaleTimeString('en-US', { timeZone: resolveTz(tzIana), hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : null,
              se.Operation || se.operation,
              who,
            ].filter(Boolean).join(' · ');
            // PHI gate: scrub Subject lines out of the raw JSON unless this event's
            // mailbox owner has been revealed (same gate + audit as the column above).
            const eventRevealed = owner && revealedOwners.has(owner);
            let seForDisplay = se;
            if (!eventRevealed) {
              const clone = JSON.parse(JSON.stringify(se));
              if (typeof clone.Folders === 'string') {
                try { clone.Folders = JSON.parse(clone.Folders); } catch { /* leave as string; scrub still walks it as a value */ }
              }
              scrubSubjectsDeep(clone);
              seForDisplay = clone;
            }
            return (
              <div key={idx} className="mb-1">
                <RawEventJsonPanel
                  title={label || `Event ${idx + 1}`}
                  rawEventJson={JSON.stringify(seForDisplay)}
                />
              </div>
            );
          })}
        </CollapsibleSection>
      )}
    </div>
  );
}
